import { z } from "zod";
import { ZDateish, ZEmailInput, // ✅ for INPUT schemas only
ZEmailLowercase, // ✅ add this to foundationSchema (or define locally)
ZMultiplacerModuleKey, ZProjectNo, } from "./foundationSchema";
/* -------------------------------------------------------------------------- */
/*  Modules enum (from MULTIPLACER_MODULES)                                   */
/* -------------------------------------------------------------------------- */
export const ZModuleAccessLevel = z.enum(["READ", "WRITE"]);
export const ZModulePermission = z.object({
    module: ZMultiplacerModuleKey,
    access: ZModuleAccessLevel,
});
/* -------------------------------------------------------------------------- */
/*  Project permissions                                                       */
/* -------------------------------------------------------------------------- */
export const ZProjectPermissions = z.object({
    buildingIds: z.array(z.string().min(1)).default([]),
    modules: z.array(ZModulePermission).default([]),
    updatedAt: ZDateish.optional(),
});
/* -------------------------------------------------------------------------- */
/*  Project invites (embedded in project doc)                                 */
/* -------------------------------------------------------------------------- */
export const ZProjectInviteStatus = z.enum(["PENDING", "ACCEPTED", "REVOKED"]);
/**
 * IMPORTANT:
 * - DB/Public schema must NOT use ZodEffects (transform/refine chains that create ZodEffects)
 *   because zod-openapi struggles to infer output types.
 * - Use ZEmailLowercase here (no transform), and normalize to lowercase in code when creating.
 */
export const ZProjectInvite = z.object({
    _id: z.any().optional(),
    email: ZEmailLowercase, // ✅ was ZEmailInput (transform) -> causes zod-openapi crash
    permissions: ZProjectPermissions.default({ buildingIds: [], modules: [] }),
    invitedBy: z.any(),
    createdAt: ZDateish,
    expiresAt: ZDateish.optional(),
    acceptedAt: ZDateish.optional(),
    acceptedBy: z.any().optional(),
    status: ZProjectInviteStatus.default("PENDING"),
});
/* -------------------------------------------------------------------------- */
/*  Project members                                                          */
/* -------------------------------------------------------------------------- */
export const ZProjectMemberRole = z.enum(["OWNER", "ADMIN", "MEMBER", "VIEWER"]);
export const ZProjectMember = z.object({
    userId: z.any(),
    displayName: z.string().optional().default(""),
    role: ZProjectMemberRole.default("MEMBER"),
    permissions: ZProjectPermissions.optional(),
    addedAt: ZDateish.optional(),
    addedBy: z.any().optional(),
});
/* -------------------------------------------------------------------------- */
/*  Base project                                                              */
/* -------------------------------------------------------------------------- */
export const ZFileAttrField = z.enum(["fileAttr1", "fileAttr2", "fileAttr3", "fileAttr4"]);
export const ZEnrichStatus = z.object({
    status: z.enum(["idle", "running", "done"]),
    total: z.number().int().min(0),
    done: z.number().int().min(0),
    failed: z.number().int().min(0).optional(),
    startedAt: ZDateish.optional().nullable(),
    finishedAt: ZDateish.optional().nullable(),
});
export const ZAirTerminalDirection = z.enum(["SUPPLY", "EXHAUST", "RETURN", "TRANSFER"]);
export const ZCompCodeDirectionRule = z.object({
    prefix: z.string().min(1),
    direction: ZAirTerminalDirection,
    description: z.string().optional(),
});
export const ZVentilationSettings = z.object({
    compCodeDirectionRules: z.array(ZCompCodeDirectionRule).default([]),
    updatedAt: ZDateish.optional(),
});
export const ZDrofusInterfaceScheduleDay = z.enum([
    "Sun",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
]);
export const ZDrofusInterfaceOccurrencesSyncSchedule = z.object({
    enabled: z.boolean().default(false),
    days: z.array(ZDrofusInterfaceScheduleDay).min(1).default(["Sun", "Mon", "Tue", "Wed", "Thu"]),
    hour: z.number().int().min(0).max(23).default(2),
    minute: z.number().int().min(0).max(59).default(0),
    timezone: z.string().min(1).default("W. Europe Standard Time"),
    cronExpression: z.string().min(1).default("0 0 2 * * Sun-Thu"),
    pageSize: z.number().int().min(100).max(2000).default(1000),
    maxRows: z.number().int().positive().max(100000).nullable().default(null),
    updatedAt: ZDateish.optional(),
    lastTriggeredAt: ZDateish.optional(),
    lastTriggeredSlot: z.string().min(1).optional(),
});
export const ZDrofusInterfaceProjectSettings = z.object({
    occurrencesSyncSchedule: ZDrofusInterfaceOccurrencesSyncSchedule.optional(),
});
export const ZProjectBase = z.object({
    _id: z.any().optional(),
    projectNo: ZProjectNo,
    title: z.string().min(1),
    compCodePath: z.string(),
    cadCompCodePath: z.string().optional(),
    pnsPath: z.string().optional(),
    areaPath: z.string().optional(),
    mmiPath: z.string().optional(),
    disciplineFileAttr: ZFileAttrField.optional(),
    enrich: ZEnrichStatus.optional(),
    createdAt: ZDateish.optional(),
    updatedAt: ZDateish.optional(),
    ventilation: ZVentilationSettings.optional(),
    drofusInterface: ZDrofusInterfaceProjectSettings.optional(),
});
/* -------------------------------------------------------------------------- */
/*  Drofus                                                                    */
/* -------------------------------------------------------------------------- */
export const ZDrofusSettingsPublic = z.object({
    drofusBaseUrl: z.string().url(),
    drofusDb: z.string(),
    drofusProject: z.string(),
    hasApiKey: z.boolean(),
    last4: z.string().min(1).max(4).optional(),
    verifiedAt: ZDateish.optional(),
    updatedAt: ZDateish.optional(),
});
export const ZEncryptedSecret = z.object({
    c: z.string(),
    iv: z.string(),
    tag: z.string(),
    last4: z.string().min(1).max(4),
    kid: z.string().optional(),
    alg: z.literal("AES-256-GCM").optional(),
    verifiedAt: ZDateish.optional(),
    updatedAt: ZDateish.optional(),
});
export const ZDrofusSettings = z.object({
    drofusBaseUrl: z.string().url(),
    drofusDb: z.string(),
    drofusProject: z.string(),
    apiKeyEnc: ZEncryptedSecret.optional(),
});
/* -------------------------------------------------------------------------- */
/*  DB schema                                                                 */
/* -------------------------------------------------------------------------- */
export const ZProjectDb = ZProjectBase.extend({
    drofus: ZDrofusSettings.optional(),
    members: z.array(ZProjectMember).default([]),
    invites: z.array(ZProjectInvite).default([]),
});
/* -------------------------------------------------------------------------- */
/*  Public schema                                                             */
/* -------------------------------------------------------------------------- */
export const ZProjectPublic = ZProjectBase.extend({
    drofus: ZDrofusSettingsPublic.optional(),
    members: z.array(ZProjectMember).default([]),
    invites: z.array(ZProjectInvite).default([]),
});
/* -------------------------------------------------------------------------- */
/*  Mapper: DB -> Public                                                      */
/* -------------------------------------------------------------------------- */
export function toPublicProject(p) {
    const base = ZProjectBase.parse(p);
    return {
        ...base,
        drofus: p.drofus
            ? {
                drofusBaseUrl: p.drofus.drofusBaseUrl,
                drofusDb: p.drofus.drofusDb,
                drofusProject: p.drofus.drofusProject,
                hasApiKey: !!p.drofus.apiKeyEnc,
                last4: p.drofus.apiKeyEnc?.last4,
                verifiedAt: p.drofus.apiKeyEnc?.verifiedAt,
                updatedAt: p.drofus.apiKeyEnc?.updatedAt,
            }
            : undefined,
        members: Array.isArray(p.members) ? p.members : [],
        invites: Array.isArray(p.invites) ? p.invites : [],
    };
}
/* -------------------------------------------------------------------------- */
/*  Helper: input schema for creating invites                                 */
/* -------------------------------------------------------------------------- */
// Use this in your tRPC route input (NOT in DB/Public schemas)
export const ZCreateProjectInviteInput = z.object({
    email: ZEmailInput, // ✅ transform to lowercase here
    permissions: ZProjectPermissions.optional(),
});
